/**
 * Reading chart data that people paste or type.
 *
 * Four shapes cover every chart on the site:
 *
 * - **Labeled rows** — `label, value[, value…]`, one category per line, optionally under
 *   a header row that names the series (bar, line and pie charts).
 * - **Column groups** — one group per column, as pasted from a spreadsheet, optionally
 *   under a header row of group names (box plots).
 * - **Row groups** — one group per line, `Name: 1, 2, 3` (box plots).
 * - **x, y pairs** — with an optional third column naming a group (scatter plots).
 *
 * Cells are separated by tabs (a spreadsheet paste), semicolons or commas, checked in
 * that order across the whole text. The order is the spreadsheet rule `parseNumberList`
 * already follows: once a semicolon appears, the comma is free to be a decimal mark, so
 * `Apples; 2,5` reads as 2.5 for the half of Europe that writes it that way. With none of
 * the three, runs of spaces separate cells.
 *
 * Numbers go through `parseNumberInput` — which also reads a typographic minus (−, the
 * sign the charts print) — after one courtesy: a trailing percent sign is dropped, so
 * `Yes, 45%` is 45.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type Delimiter = "\t" | ";" | "," | " ";

const LINE_BREAK = /\r\n|\r|\n/;
const WHITESPACE_RUN = /\s+/;
const TRAILING_PERCENT = /\s*%$/;

/** A non-blank line and its 1-based line number, for error messages. */
export interface SourceLine {
  text: string;
  line: number;
}

export function sourceLines(text: string): SourceLine[] {
  const lines: SourceLine[] = [];
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() !== "") {
      lines.push({ text: raw, line: index + 1 });
    }
  }
  return lines;
}

/** Tabs, then semicolons, then commas; spaces when the text has none of those. */
export function detectDelimiter(lines: readonly string[]): Delimiter {
  for (const candidate of ["\t", ";", ","] as const) {
    if (lines.some((line) => line.includes(candidate))) {
      return candidate;
    }
  }
  return " ";
}

/** A number typed into a cell, or null. */
export function parseCell(raw: string): number | null {
  return parseNumberInput(raw.trim().replace(TRAILING_PERCENT, ""));
}

/** One delimited line, honouring double-quoted cells (`"Smith, J.", 12`). */
function splitQuoted(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  let index = 0;
  while (index < line.length) {
    const char = line[index];
    if (quoted && char === '"' && line[index + 1] === '"') {
      current += '"';
      index += 2;
      continue;
    }
    if (char === '"' && (quoted || current.trim() === "")) {
      quoted = !quoted;
      if (quoted) {
        current = "";
      }
    } else if (char === delimiter && !quoted) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
    index += 1;
  }
  cells.push(current.trim());
  return cells;
}

export function splitCells(line: string, delimiter: Delimiter): string[] {
  if (delimiter === " ") {
    return line.trim().split(WHITESPACE_RUN).filter(Boolean);
  }
  return splitQuoted(line, delimiter);
}

/* ------------------------------------------------------------- labeled rows */

export interface LabeledRow {
  label: string;
  /** One entry per series; null where the cell was empty or not a number. */
  values: (number | null)[];
  line: number;
}

export interface LabeledTable {
  /** The header row's cells when the first line names the columns. */
  header: string[] | null;
  /**
   * Whether the first line looks like a header on its own (it holds no numbers) — what
   * a "first line is a header" checkbox should show before the reader touches it.
   */
  autoHeader: boolean;
  delimiter: Delimiter;
  /** How many value columns (series) hold at least one number. */
  seriesCount: number;
  rows: LabeledRow[];
  /** Line numbers that held no number at all and were left out. */
  skipped: number[];
  /** Non-empty cells that were not numbers (read as missing). */
  invalidCells: number;
}

/**
 * Split a line into its label and its value cells. With spaces as the separator, the
 * trailing run of numbers are the values and the words before them the label, so
 * `New York 8.3` is ("New York", [8.3]) and `2019 45` is ("2019", [45]).
 */
function labelAndCells(
  text: string,
  delimiter: Delimiter
): { label: string; cells: string[] } {
  const cells = splitCells(text, delimiter);
  if (delimiter !== " ") {
    return { label: cells[0] ?? "", cells: cells.slice(1) };
  }
  let first = cells.length;
  while (first > 0 && parseCell(cells[first - 1]) !== null) {
    first -= 1;
  }
  const labelEnd = Math.max(1, first);
  return {
    label: cells.slice(0, labelEnd).join(" "),
    cells: cells.slice(labelEnd),
  };
}

/**
 * Rows of `label, value[, value…]`.
 *
 * Whether the first line is a header is detected — a first line with no numbers is one —
 * unless `header` says otherwise. The override exists because detection cannot see a
 * header made of numbers: in `Department, 2023, 2024` the years name the series, but
 * the line is shaped exactly like data.
 */
export function parseLabeledRows(
  text: string,
  { header: forced }: { header?: boolean } = {}
): LabeledTable {
  const lines = sourceLines(text);
  const delimiter = detectDelimiter(lines.map((entry) => entry.text));
  const rows: LabeledRow[] = [];
  const skipped: number[] = [];
  let header: string[] | null = null;
  let autoHeader = false;
  let invalidCells = 0;
  for (const [index, entry] of lines.entries()) {
    const { label, cells } = labelAndCells(entry.text, delimiter);
    const values = cells.map(parseCell);
    const numeric = values.filter((value) => value !== null).length;
    if (index === 0) {
      autoHeader = numeric === 0;
      if (forced ?? autoHeader) {
        header = splitCells(entry.text, delimiter);
        continue;
      }
    }
    if (numeric === 0) {
      skipped.push(entry.line);
      continue;
    }
    invalidCells += cells.filter(
      (cell, column) => cell !== "" && values[column] === null
    ).length;
    rows.push({ label, values, line: entry.line });
  }
  let seriesCount = 0;
  for (const row of rows) {
    for (const [column, value] of row.values.entries()) {
      if (value !== null) {
        seriesCount = Math.max(seriesCount, column + 1);
      }
    }
  }
  for (const row of rows) {
    row.values = Array.from(
      { length: seriesCount },
      (_, column) => row.values[column] ?? null
    );
  }
  return {
    header,
    autoHeader,
    delimiter,
    seriesCount,
    rows,
    skipped,
    invalidCells,
  };
}

/**
 * Series names from the header row, falling back to `fallback(index)` for a column the
 * header does not name. A space-separated header is only trusted when it has exactly
 * one word per column, since "Mean score" would otherwise become two series.
 */
export function seriesNames(
  table: LabeledTable,
  fallback: (index: number) => string
): string[] {
  const { header, seriesCount, delimiter } = table;
  const usable =
    header !== null && (delimiter !== " " || header.length === seriesCount + 1);
  return Array.from({ length: seriesCount }, (_, index) => {
    const name = usable ? header[index + 1]?.trim() : "";
    return name ? name : fallback(index);
  });
}

/* ------------------------------------------------------------ column groups */

export interface Groups {
  /** Group names, or null where the input names none. */
  names: (string | null)[];
  /** Whether the first line looks like a header on its own (see `LabeledTable`). */
  autoHeader: boolean;
  groups: number[][];
  /** Non-empty cells or lines that held no readable number. */
  invalid: number;
}

/**
 * One group per column, with an optional header row of names — detected when the first
 * line has a cell that is not a number, unless `header` says otherwise.
 */
export function parseColumnGroups(
  text: string,
  { header: forced }: { header?: boolean } = {}
): Groups {
  const lines = sourceLines(text);
  const delimiter = detectDelimiter(lines.map((entry) => entry.text));
  let headerCells: string[] = [];
  let autoHeader = false;
  const columns: number[][] = [];
  let invalid = 0;
  for (const [index, entry] of lines.entries()) {
    const cells = splitCells(entry.text, delimiter);
    const values = cells.map(parseCell);
    if (index === 0) {
      autoHeader = cells.some(
        (cell, column) => cell !== "" && values[column] === null
      );
      if (forced ?? autoHeader) {
        headerCells = cells;
        continue;
      }
    }
    for (const [column, value] of values.entries()) {
      columns[column] ??= [];
      if (value !== null) {
        columns[column].push(value);
      } else if (cells[column] !== "") {
        invalid += 1;
      }
    }
  }
  const width = Math.max(headerCells.length, columns.length);
  const names: (string | null)[] = [];
  const groups: number[][] = [];
  for (let column = 0; column < width; column++) {
    const values = columns[column] ?? [];
    if (values.length === 0) {
      continue;
    }
    names.push(headerCells[column]?.trim() || null);
    groups.push(values);
  }
  return { names, autoHeader, groups, invalid };
}

/**
 * One group per line: `Name: 1, 2, 3`, `Name, 1, 2, 3`, or just the numbers. Each line
 * picks its own separator, since each line is a separate list — `3 4 5` under a line
 * written with commas is still three numbers, not 345.
 */
export function parseRowGroups(text: string): Groups {
  const names: (string | null)[] = [];
  const groups: number[][] = [];
  let invalid = 0;
  for (const entry of sourceLines(text)) {
    const colon = entry.text.indexOf(":");
    let name: string | null = null;
    let cells: string[];
    if (colon >= 0) {
      name = entry.text.slice(0, colon).trim() || null;
      const rest = entry.text.slice(colon + 1);
      cells = splitCells(rest, detectDelimiter([rest]));
    } else {
      cells = splitCells(entry.text, detectDelimiter([entry.text]));
      if (cells.length > 0 && parseCell(cells[0]) === null) {
        name = cells[0] || null;
        cells = cells.slice(1);
      }
    }
    const values = cells.map(parseCell);
    const numbers = values.filter((value): value is number => value !== null);
    invalid += cells.filter(
      (cell, index) => cell !== "" && values[index] === null
    ).length;
    if (numbers.length === 0) {
      invalid += 1;
      continue;
    }
    names.push(name);
    groups.push(numbers);
  }
  return { names, autoHeader: false, groups, invalid };
}

/* ----------------------------------------------------------------- x, y pairs */

export interface XYPoint {
  x: number;
  y: number;
  group: string | null;
  line: number;
}

export interface XYTable {
  /** Header cells (x name, y name, group name) when the first line has them. */
  header: string[] | null;
  points: XYPoint[];
  skipped: number[];
}

export function parseXYRows(text: string): XYTable {
  const lines = sourceLines(text);
  const delimiter = detectDelimiter(lines.map((entry) => entry.text));
  const points: XYPoint[] = [];
  const skipped: number[] = [];
  let header: string[] | null = null;
  for (const [index, entry] of lines.entries()) {
    const cells = splitCells(entry.text, delimiter);
    const x = parseCell(cells[0] ?? "");
    const y = parseCell(cells[1] ?? "");
    if (x === null || y === null) {
      if (index === 0) {
        header = cells;
      } else {
        skipped.push(entry.line);
      }
      continue;
    }
    const group = cells.slice(2).join(" ").trim();
    points.push({ x, y, group: group || null, line: entry.line });
  }
  return { header, points, skipped };
}

/* -------------------------------------------------------------- number lists */

const SEMICOLON_SEPARATORS = /[\s;]+/;
const COMMA_SEPARATORS = /[\s,;]+/;

/**
 * A list of numbers, with the tokens that were not numbers.
 *
 * The same separator rule as `parseNumberList` (a semicolon anywhere frees the comma to
 * be a decimal mark), but it reports what it could not read instead of dropping it
 * silently, so a chart can say "2 entries were not numbers".
 */
export function parseNumberTokens(text: string): {
  values: number[];
  invalid: string[];
} {
  const separators = text.includes(";")
    ? SEMICOLON_SEPARATORS
    : COMMA_SEPARATORS;
  const values: number[] = [];
  const invalid: string[] = [];
  for (const token of text.split(separators)) {
    if (token === "") {
      continue;
    }
    const value = parseCell(token);
    if (value === null) {
      invalid.push(token);
    } else {
      values.push(value);
    }
  }
  return { values, invalid };
}

/**
 * Assign points to at most `max` named groups, in order of first appearance. Groups
 * past the limit share the last slot under `otherLabel` — a ninth colour would repeat
 * the first. A point with no group gets `ungroupedLabel` when other points have one.
 */
export function groupIndex(
  groups: readonly (string | null)[],
  {
    max,
    otherLabel,
    ungroupedLabel,
  }: { max: number; otherLabel: string; ungroupedLabel: string }
): { names: string[]; index: number[]; folded: number } {
  const anyNamed = groups.some((group) => group !== null);
  if (!anyNamed) {
    return { names: [], index: groups.map(() => 0), folded: 0 };
  }
  const seen = new Map<string, number>();
  const order: string[] = [];
  for (const group of groups) {
    const name = group ?? ungroupedLabel;
    if (!seen.has(name)) {
      seen.set(name, order.length);
      order.push(name);
    }
  }
  const folded = Math.max(0, order.length - max);
  const names = folded > 0 ? [...order.slice(0, max - 1), otherLabel] : order;
  const index = groups.map((group) =>
    Math.min(seen.get(group ?? ungroupedLabel) ?? 0, names.length - 1)
  );
  return { names, index, folded: folded > 0 ? folded + 1 : 0 };
}
